import { useRef, useEffect, useState, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCamera, faX, faRotateRight } from "@fortawesome/free-solid-svg-icons";

const CameraModal = ({ isOpen, onClose, onCapture, title = "التقاط صورة" }) => {
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const [facingMode, setFacingMode] = useState("user"); // 'user' for front camera, 'environment' for back camera
    const [error, setError] = useState(null);

    const startCamera = useCallback(async () => {
        try {
            setError(null);

            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
            }

            const constraints = {
                video: {
                    facingMode,
                    width: { ideal: 1280 },
                    height: { ideal: 720 }
                }
            };

            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            streamRef.current = stream;

            if (videoRef.current) {
                videoRef.current.srcObject = stream;
            }
        } catch (err) {
            console.error("Error accessing camera:", err);
            setError("فشل في الوصول إلى الكاميرا. يرجى التحقق من الصلاحيات.");
        }
    }, [facingMode]);

    const stopCamera = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
    }, []);

    useEffect(() => {
        if (isOpen) {
            startCamera();
        } else {
            stopCamera();
        }

        return () => stopCamera();
    }, [isOpen, facingMode, startCamera, stopCamera]);

    const capturePhoto = () => {
        if (!videoRef.current) return;

        const canvas = document.createElement("canvas");
        const video = videoRef.current;

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        // Convert to blob
        canvas.toBlob((blob) => {
            if (blob) {
                const file = new File([blob], `photo_${Date.now()}.jpg`, { type: "image/jpeg" });
                onCapture(file);
                stopCamera();
                onClose();
            }
        }, "image/jpeg", 0.9);
    };

    const switchCamera = () => {
        setFacingMode(prev => prev === "user" ? "environment" : "user");
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
            <div className="relative h-full bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
                {/* Header */}
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold text-gray-800">{title}</h2>
                    <button
                        onClick={() => {
                            stopCamera();
                            onClose();
                        }}
                        className="text-gray-600 hover:text-gray-800"
                    >
                        <FontAwesomeIcon icon={faX} className="text-2xl" />
                    </button>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                        {error}
                    </div>
                )}

                {/* Video Preview */}
                <div className="relative bg-black rounded-lg overflow-hidden mb-4" style={{ aspectRatio: "4/3" }}>
                    <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        className="w-full h-full object-cover"
                    />
                </div>

                {/* Controls */}
                <div className="flex justify-center gap-4">
                    <button
                        onClick={switchCamera}
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2"
                    >
                        <FontAwesomeIcon icon={faRotateRight} />
                        تبديل الكاميرا
                    </button>
                    <button
                        onClick={capturePhoto}
                        className="px-6 py-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 flex items-center gap-2 text-lg"
                    >
                        <FontAwesomeIcon icon={faCamera} />
                        التقاط
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CameraModal;

